import React, { useRef, createContext } from "react";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import { useToast, Tooltip, IconButton, Icon, HStack, Breadcrumb, BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/react";
import { MdDelete, MdAdd } from "react-icons/md";
import { useApiGet, useCacheState, useStickyState } from "hooks";
import { HashButton, DataTable, Dialog, ActiveButton } from "components";
import { messages } from "consts";
import { api, translator } from "lib";
import Filter from "./filter";

export const UsersListContext = createContext();

export const UsersList = () => {
  const navigate = useNavigate();
  const [perPage, setPerPage] = useCacheState(20, "UsersList:perPage");
  const [sort, setSort] = useStickyState({ name: 1 }, "UsersList:sort");
  const [page, setPage] = useCacheState(0, "UsersList:page");
  const [search, setSearch] = useCacheState("", "UsersList:search");
  const [query, setQuery] = useCacheState({ anonymous: false, active: true }, "UsersList:query");
  const [response, loading, refresh, error] = useApiGet("/users", { search, query, perPage, page, sort });
  const toast = useToast();
  const deleteRef = useRef();

  const handleDelete = async (item) => {
    try {
      await api.delete(`/users/${item._id}`);
      toast({ description: messages.success.deleteData, status: "success", isClosable: true });
      refresh();
    } catch (error) {
      toast({ description: error.message, status: "error", isClosable: true });
    }
  };

  return (
    <UsersListContext.Provider value={{ query, setQuery, setPage, loading }}>
      <HStack py="5px" px="10px" justify="space-between">
        <Breadcrumb fontWeight="medium" fontSize="xs">
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/home">
              Home
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink as={Link} to="/users">
              Usuários
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <Tooltip label="Novo">
          <IconButton size="sm" colorScheme="main" as={Link} to="/users/details" icon={<Icon as={MdAdd} boxSize="22px" />} />
        </Tooltip>
      </HStack>

      <Filter />

      <DataTable
        tableId="UsersList:table"
        title="Usuários"
        data={response?.data ?? []}
        size={response?.size ?? 0}
        loading={loading}
        sort={sort}
        onSort={setSort}
        perPage={perPage}
        onPerPage={setPerPage}
        page={page}
        onPaginate={setPage}
        search={search}
        onSearch={setSearch}
        onRefresh={refresh}
        error={error}
        renderItems={[
          { key: "_id", label: "ID", visible: true, render: (value) => <HashButton _id={value} /> },
          { key: "name", label: "Nome", visible: true },
          { key: "document", label: "CPF", visible: true },
          { key: "phone", label: "Celular", visible: true },
          { key: "email", label: "E-mail", visible: true },
          { key: "role", label: "Nível", visible: true, formatter: translator },
          { key: "platform.os", label: "S.O.", visible: true },
          { key: "platform.osVersion", label: "Versão do S.O.", visible: true },
          { key: "platform.appVersion", label: "Versão do App", visible: true },
          { key: "platform.brand", label: "Marca", visible: true },
          { key: "platform.model", label: "Modelo", visible: true },
          { key: "birth", label: "Nascimento", visible: false, formatter: (value) => (value ? moment(value).format("DD/MM/YYYY") : "-") },
          {
            key: "active",
            label: "Ativo?",
            visible: true,
            textAlign: "center",
            render: (value) => <ActiveButton isActive={value} />,
            exporter: (value) => (value ? "Sim" : "Não"),
          },
          { key: "address.zipCode", label: "CEP", visible: false },
          { key: "address.street", label: "Rua", visible: false },
          { key: "address.number", label: "Número", visible: false },
          { key: "address.complement", label: "Complemento", visible: false },
          { key: "address.neighborhood", label: "Bairro", visible: false },
          { key: "address.city", label: "Cidade", visible: false },
          { key: "address.state", label: "Estado", visible: false },
          {
            key: "createdAt",
            label: "Criação",
            visible: true,
            formatter: (value) => moment(value).format("DD/MM/YYYY HH:mm:ss"),
          },
          {
            key: "updatedAt",
            label: "Atualização",
            visible: false,
            formatter: (value) => moment(value).format("DD/MM/YYYY HH:mm:ss"),
          },
        ]}
        ActionsComponent={({ item }) => (
          <Tooltip label="Excluir">
            <IconButton
              icon={<Icon as={MdDelete} />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={() => deleteRef.current.confirm(item, handleDelete)}
            />
          </Tooltip>
        )}
        onRowDoubleClick={({ _id }) => navigate(`/users/details/${_id}`)}
      />
      <Dialog.Delete ref={deleteRef} />
    </UsersListContext.Provider>
  );
};
